/**
 * Home Page Component
 * Dashboard overview with goal progress and recent sessions
 * Redesigned with improved UX hierarchy
 */

import { useState, useEffect } from 'react';
import { GoalProgressCard } from '@/components/goals/GoalProgressCard';
import { TodaySessionsList } from '@/components/sessions/TodaySessionsList';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, History } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { OneDayOneAyahDialog } from '@/components/home/OneDayOneAyahDialog';
import { isTodayDismissed } from '@/services/oneDayOneAyah.service';
import { getHijriToday, type HijriTodayResponse } from '@/services/hijri.service';

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [oneDayOneAyahOpen, setOneDayOneAyahOpen] = useState(false);
  const [hijri, setHijri] = useState<HijriTodayResponse | null>(null);

  // Auto-open the drawer once if not hidden for today
  useEffect(() => {
    if (!isTodayDismissed()) {
      setOneDayOneAyahOpen(true);
    }
    
    // Fetch today's Hijri date
    getHijriToday().then(setHijri);
  }, []);
  
  // ดึงชื่อจาก Google OAuth metadata หรือใช้อีเมลเป็นทางเลือก
  const displayName = 
    user?.user_metadata?.full_name || 
    user?.user_metadata?.name || 
    user?.email?.split('@')[0] || 
    'ผู้ใช้';

  return (
    <div className="p-4 pb-24 space-y-6">
      {/* Hero Section - Primary Level */}
      <section className="relative overflow-hidden rounded-3xl bg-linear-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6">
        <div className="relative z-10 flex flex-row items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-1 h-12 bg-primary rounded-full hidden sm:block" />
              <div className="flex flex-col items-start gap-1">
                {hijri && (
                  <span className="text-xs font-bold text-primary/80 uppercase tracking-widest leading-none">
                    {hijri.day} {hijri.monthName} {hijri.year} AH
                  </span>
                )}
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                  สลาม, {displayName}
                </h1>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOneDayOneAyahOpen(true)}
            className="shrink-0 flex flex-col items-center justify-center gap-1 w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity"
          >
            <img src="/oneday-logo-blue.png" alt="One Day One Ayah Logo" className="w-full h-full object-cover dark:hidden" />
            <img src="/oneday-logo.png" alt="One Day One Ayah Logo" className="w-full h-full object-cover hidden dark:block" />
          </button>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -left-10 -top-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
      </section>

      {/* Goal Progress - Secondary Level */}
      <section className="space-y-3">
        <GoalProgressCard />
      </section>

      {/* Quick Actions - Tertiary Level */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-foreground px-1">
          สิ่งที่คุณสามารถทำได้
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <Link to="/read" className="h-full">
            <Card className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 hover:border-primary/50 h-full",
              "bg-linear-to-br from-primary/10 to-transparent border-primary/30"
            )}>
              <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2 h-full">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center relative">
                  <BookOpen className="w-6 h-6 text-primary" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-pulse" />
                </div>
                <span className="text-sm font-semibold text-foreground">เริ่มอ่าน<br/>อัลกุรอาน</span>
              </CardContent>
            </Card>
          </Link>

          {/* One Day One Ayah Card – always visible */}
          <Card 
            className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 hover:border-primary/50 h-full"
            )}
            onClick={() => setOneDayOneAyahOpen(true)}
          >
            <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2 h-full">
              <div className="w-12 h-12 flex items-center justify-center relative overflow-hidden">
                <img src="/oneday-logo-blue.png" alt="One Day One Ayah Logo" className="w-full h-full object-cover dark:hidden" />
                <img src="/oneday-logo.png" alt="One Day One Ayah Logo" className="w-full h-full object-cover hidden dark:block" />
              </div>
              <span className="text-sm font-semibold text-foreground">One Day One Ayah</span>
            </CardContent>
          </Card>

          {/* Hijri Calendar Card */}
          {/* Hijri Calendar Card */}
          <Link to="/hijri-calendar" className="h-full">
            <Card className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 hover:border-primary/50 h-full"
            )}>
              <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2 h-full">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center border border-primary/20 overflow-hidden shadow-sm">
                  {hijri ? (
                    <>
                      <div className="w-full bg-primary/20 py-0.5 text-[8px] font-bold uppercase tracking-tighter text-primary">
                        {hijri.monthName.slice(0, 3)}
                      </div>
                      <span className="text-xl font-bold text-primary leading-none py-1">
                        {hijri.day}
                      </span>
                    </>
                  ) : (
                    <div className="w-full h-full animate-pulse bg-primary/5" />
                  )}
                </div>
                <span className="text-sm font-semibold text-foreground">ปฏิทิน<br />ฮิจเราะห์</span>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Today's Sessions - Quaternary Level */}
      <section className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-muted-foreground" />
            <h3 className="text-base font-semibold text-foreground">
              เซสชันการอ่านวันนี้
            </h3>
          </div>
        </div>
        <TodaySessionsList />
      </section>

      {/* One Day One Ayah Drawer */}
      <OneDayOneAyahDialog
        open={oneDayOneAyahOpen}
        onClose={() => setOneDayOneAyahOpen(false)}
        onShareClick={(state) => {
          setOneDayOneAyahOpen(false);
          navigate(`/share/ayah/${state.ayah.surahNumber}/${state.ayah.numberInSurah}`, {
            state,
          });
        }}
      />
    </div>
  );
}

export default HomePage;
